import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import type { EventLogEntry } from '../types/event-log.js';

export interface DeviceEventsParams {
  deviceId: string;
  page?: number;
  pageSize?: number;
  messageType?: string | string[];
  messageTypes?: string[];
  from?: string;
  to?: string;
}

export interface DeviceEventsResponse {
  events: EventLogEntry[];
  total: number;
}

const fetchDeviceEvents = async (params: DeviceEventsParams): Promise<DeviceEventsResponse> => {
  const { deviceId, page = 1, pageSize = 20, messageType, messageTypes, from, to } = params;
  const searchParams = new URLSearchParams();

  searchParams.append('page', String(page));
  searchParams.append('pageSize', String(pageSize));

  const normalizedMessageTypes = messageTypes ?? (messageType ? [messageType].flat() : undefined);

  if (normalizedMessageTypes?.length) {
    normalizedMessageTypes.forEach((type) => {
      if (type) {
        searchParams.append('messageType', type);
      }
    });
  }

  if (from) {
    searchParams.append('from', new Date(from).toISOString());
  }
  if (to) {
    searchParams.append('to', new Date(to).toISOString());
  }

  const query = searchParams.toString();
  const url = query ? `/devices/${deviceId}/events?${query}` : `/devices/${deviceId}/events`;

  try {
    const { data } = await apiClient.get<DeviceEventsResponse>(url);
    return {
      events: data?.events ?? [],
      total: data?.total ?? 0,
    };
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch events');
  }
};

export const useDeviceEvents = (params: DeviceEventsParams) => {
  return useQuery({
    queryKey: ['device-events', params],
    queryFn: () => fetchDeviceEvents(params),
  });
};
